import { BarChart3, Briefcase, Calendar, Home, LogOut, Menu, Users } from "lucide-react";
import { useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SchedoraLogo } from "@/components/schedora/SchedoraLogo";
import { useApp } from "@/context/AppContext";
import { cn } from "@/lib/utils";
import { NavLink } from "@/components/NavLink";

const navItems = [
  { to: "/admin/dashboard", label: "Overview", icon: Home },
  { to: "/admin/businesses", label: "Businesses", icon: Briefcase },
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/bookings", label: "Bookings", icon: Calendar },
];

export default function AdminLayout() {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { currentUser, logout } = useApp();

  return (
    <div className="min-h-screen flex bg-[#f7f7f5]">
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/20 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 border-r border-black/5 bg-white flex flex-col transition-transform lg:relative lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="flex h-16 items-center gap-3 border-b px-5">
          <SchedoraLogo iconClassName="h-6 w-6" textClassName="text-lg" />
          <span className="rounded-full bg-black px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-white">Admin</span>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setSidebarOpen(false)}
              className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-950"
              activeClassName="bg-slate-950 text-white hover:bg-slate-950 hover:text-white"
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t px-3 py-4">
          <button
            type="button"
            onClick={() => void logout()}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-950"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-black/5 bg-white/90 px-4 backdrop-blur lg:px-6">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(true)}>
            <Menu className="h-5 w-5" />
          </Button>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">Schedora Platform</p>
            <p className="text-sm text-slate-600">{location.pathname.replace("/admin/", "").replace("/", " / ") || "dashboard"}</p>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <div className="rounded-full bg-slate-950 p-2 text-white">
              <BarChart3 className="h-4 w-4" />
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-slate-950">{currentUser?.displayName ?? "Super Admin"}</p>
              <p className="text-xs text-slate-500">{currentUser?.email ?? ""}</p>
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
